// src/pages/mc/demand-step-info.ts
//
// DEM-2001 — derivação canônica de "Etapa X/Y" + ATOR a partir do pipeline run
// da demanda. Extraído do MCDemandsPage pra ser testável em isolamento
// (regressão de progresso/ator com DEM-1998/1999/2000).
//
// Regra-mãe (DEM-2001): a etapa e o ator REAIS vêm dos campos derivados
// server-side da pipeline_run (current_step_slug / current_step_number /
// steps_total). O fallback heurístico (projeção tasks_done/tasks_planned sobre
// os steps do config) é SUBORDINADO: só roda quando o servidor não derivou a
// etapa, e fica marcado (fromServer=false) pra a UI poder sinalizar.
//
// DEM-2036 — duas correções no contador "Etapa X/Y":
//   (a) DENOMINADOR ESTÁVEL: o total passa a ser o total CANÔNICO do pipeline
//       (engenharia-arqueops = 6 steps), não o config.steps.length / seed de
//       pipeline_run_steps daquele run específico. Runs de versões diferentes
//       do pipeline (ou com skip_consensus) tinham steps_total divergente
//       (4, 5, 6...) e o denominador "reduzia" entre cards. Agora é fixo no
//       total do pipeline.
//   (b) SEMPRE VISÍVEL: o fallback não retorna mais null por falta de config
//       (o board carrega os runs em modo LIGHT, sem config). Com run presente,
//       sempre devolve um DemandStepInfo (total canônico + melhor estimativa de
//       current), pra o badge nunca sumir de uma demanda em execução.
//
// DEM-2051 — o badge passa a refletir a ETAPA REAL do pipeline, NUNCA contagem
// de tasks. Cravado por Breno (fonte primária máxima — observado ao vivo):
//   - numerador X = current_step_number (índice 1-based do step corrente da
//     run, derivado server-side de pipeline_run_steps);
//   - denominador Y = steps_total (total de etapas do pipeline) / total canônico;
//   - nome da etapa = stepNameForSlug(current_step_slug).
//   FALLBACK por tasks ELIMINADO: o antigo
//   `Math.floor(tasks_done / tasks_planned * total) + 1` mentia (andava com
//   cada task que concluía e fabricava nome null → o nome da etapa sumia do
//   card). Quando o servidor não derivou a etapa (step genuinamente
//   indisponível), devolvemos estado NEUTRO (current=null → a UI mostra "—"),
//   NUNCA um número fabricado a partir de contagem de tasks.

import type { DemandStepInfo } from './DemandRichCard';

/** Subconjunto de PipelineRunSummary consumido aqui (mantém o módulo leve e
 *  testável sem arrastar o type inteiro da api-pipelines). */
export interface RunStepSource {
  config?: Record<string, unknown> | null;
  tasks_planned: number;
  tasks_done: number;
  steps_total?: number | null;
  current_step_number?: number | null;
  current_step_slug?: string | null;
  /** Nome LEGÍVEL do pipeline da run (pipelines.name), derivado server-side
   *  (DEM-2051). Segunda linha do card exibe-o em vez do slug cru. */
  pipeline_name?: string | null;
}

/**
 * Ordem canônica dos 6 steps do pipeline engenharia-arqueops (único pipeline
 * global). DEM-2036: o TOTAL de etapas exibido é SEMPRE este (6) — estável,
 * independente do config/seed de cada run. Derivado da ordem (não número solto)
 * pra ficar em sincronia com os mapas de ator/nome abaixo.
 */
const CANONICAL_PIPELINE_STEPS = [
  'consensus-validation',
  'demand-refinement',
  'development',
  'auto-review',
  'peer-review',
  'commit-and-merge',
] as const;
const CANONICAL_PIPELINE_TOTAL = CANONICAL_PIPELINE_STEPS.length; // 6

/**
 * Mapa canônico slug-do-step → ator (agente) que atua nele. Cobre o pipeline
 * engenharia-arqueops (v5.x, 6 steps) e os aliases com hífen/underscore que o
 * backend usa em pipeline_step_id. Steps desconhecidos caem no fallback de
 * formatação do slug (sem ator) — nunca um ator chutado.
 *
 * NOTA (DEM-2036): o ator deixou de ser RENDERIZADO no card (removido da UI),
 * mas continua DERIVADO aqui — é dado consumido por testes/outras superfícies.
 */
const STEP_ACTOR_BY_SLUG: Record<string, string> = {
  'consensus-validation': 'Josué',
  consensus_validation: 'Josué',
  'demand-refinement': 'Josué',
  demand_refinement: 'Josué',
  development: 'Bezalel',
  'auto-review': 'Bezalel',
  auto_review: 'Bezalel',
  'peer-review': 'Jeremias',
  peer_review: 'Jeremias',
  'commit-and-merge': 'DevOps',
  commit_and_merge: 'DevOps',
};

/** Nome amigável do step por slug (fallback quando o config não traz `name`). */
const STEP_NAME_BY_SLUG: Record<string, string> = {
  'consensus-validation': 'Consensus Validation',
  consensus_validation: 'Consensus Validation',
  'demand-refinement': 'Demand Refinement',
  demand_refinement: 'Demand Refinement',
  development: 'Development',
  'auto-review': 'Auto Review',
  auto_review: 'Auto Review',
  'peer-review': 'Peer Review',
  peer_review: 'Peer Review',
  'commit-and-merge': 'Commit and Merge',
  commit_and_merge: 'Commit and Merge',
};

/** Normaliza slug (underscore→hífen) pro match canônico. */
function normalizeSlug(slug: string): string {
  return slug.replace(/_/g, '-');
}

/** true quando o slug pertence ao pipeline canônico (engenharia-arqueops). */
function isCanonicalSlug(slug: string | null | undefined): boolean {
  if (!slug) return false;
  return (CANONICAL_PIPELINE_STEPS as readonly string[]).includes(normalizeSlug(slug));
}

/** Ator do step corrente a partir do slug canônico (DEM-2001). null se
 *  desconhecido — a UI não inventa ator. */
export function actorForStepSlug(slug: string | null | undefined): string | null {
  if (!slug) return null;
  return STEP_ACTOR_BY_SLUG[slug] ?? null;
}

function stepNameForSlug(slug: string | null | undefined): string | null {
  if (!slug) return null;
  return STEP_NAME_BY_SLUG[slug] ?? null;
}

function configSteps(run: RunStepSource): Array<{ id?: string; name?: string }> {
  return (run.config?.['steps'] as Array<{ id?: string; name?: string }> | undefined) ?? [];
}

/**
 * Total de etapas ESTÁVEL (DEM-2036). Pro pipeline canônico é SEMPRE
 * CANONICAL_PIPELINE_TOTAL (6), ignorando o steps_total/config volátil do run.
 * Pra runs de outro pipeline (defensivo — hoje engenharia-arqueops é o único),
 * usa o steps_total do servidor ou o tamanho do config; em último caso, o total
 * canônico evita um denominador 0 (badge sempre visível).
 */
function stableTotal(run: RunStepSource, steps: Array<{ id?: string; name?: string }>): number {
  const canonical =
    isCanonicalSlug(run.current_step_slug) || steps.some((s) => isCanonicalSlug(s.id));
  if (canonical) return CANONICAL_PIPELINE_TOTAL;
  if (run.steps_total != null && run.steps_total > 0) return run.steps_total;
  if (steps.length > 0) return steps.length;
  return CANONICAL_PIPELINE_TOTAL;
}

/**
 * "Etapa X/Y" + nome do step + nome do pipeline + ATOR a partir do pipeline run
 * da demanda.
 *
 * DEM-2051 — fonte ÚNICA da etapa = campos server-side canônicos
 * (current_step_number = índice 1-based do step CORRENTE da run; current_step_slug;
 * steps_total = total de etapas do pipeline). NUNCA contagem de tasks.
 *   - numerador = current_step_number;
 *   - denominador = total canônico do pipeline (stableTotal — engenharia-arqueops = 6);
 *   - nome da etapa = nome do config OU stepNameForSlug(current_step_slug) — em
 *     run viva com slug presente, SEMPRE não-null (corrige o sumiço do nome).
 *
 * Quando o servidor NÃO derivou a etapa (current_step_number nulo — step
 * genuinamente indisponível), devolve estado NEUTRO: current=null. A UI mostra
 * "—" no contador e omite o nome da etapa — JAMAIS fabrica um número a partir
 * de tasks_done/tasks_planned (fallback removido).
 *
 * Sem run → null (não há badge).
 */
export function stepInfoFromRun(run: RunStepSource | undefined | null): DemandStepInfo | null {
  if (!run) return null;
  const steps = configSteps(run);
  const total = stableTotal(run, steps);
  const pipelineName = run.pipeline_name ?? null;

  // Caminho ÚNICO — etapa REAL derivada server-side. fromServer=true.
  if (run.current_step_number != null && run.current_step_number > 0) {
    const slug = run.current_step_slug ?? null;
    // DEM-2057 — label↔número↔slug de FONTE ÚNICA. O nome da etapa deve derivar
    // do MESMO step que o número/slug (current_step_slug, derivado server-side de
    // pipeline_run_steps), NUNCA do config indexado por current_step_number-1: a
    // ordem do config pode divergir da ordem real da run (backward churn / versões
    // diferentes), e indexar o config pelo número exibiria o NOME de OUTRO step
    // (ex.: label "Commit and Merge" enquanto a etapa real é peer-review). O nome
    // do config só é usado quando o step do config NAQUELE índice é EXATAMENTE o
    // slug corrente (mesma fonte). Caso contrário, prioriza stepNameForSlug(slug).
    const configStep = steps[run.current_step_number - 1];
    const configNameMatchesSlug =
      slug != null && configStep?.id != null && normalizeSlug(configStep.id) === normalizeSlug(slug);
    const name =
      stepNameForSlug(slug) ??
      (configNameMatchesSlug ? configStep?.name : undefined) ??
      slug ??
      (configNameMatchesSlug ? configStep?.id : undefined) ??
      null;
    return {
      current: Math.min(run.current_step_number, total),
      total,
      name,
      slug,
      actor: actorForStepSlug(slug),
      pipelineName,
      fromServer: true,
    };
  }

  // Estado NEUTRO (DEM-2051): servidor não derivou a etapa → "—". Sem fallback
  // por tasks. O total canônico segue presente (denominador estável), mas o
  // numerador é null e o nome da etapa fica indisponível — a UI renderiza "—".
  return {
    current: null,
    total,
    name: null,
    slug: run.current_step_slug ?? null,
    actor: actorForStepSlug(run.current_step_slug ?? null),
    pipelineName,
    fromServer: false,
  };
}
